import { ApplicationConfig, APP_INITIALIZER, ErrorHandler, inject, importProvidersFrom } from '@angular/core';
import { provideRouter, withInMemoryScrolling } from '@angular/router';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideStore } from '@ngrx/store';
import { provideEffects } from '@ngrx/effects';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { provideAnimations } from '@angular/platform-browser/animations';
import { ToastrModule } from 'ngx-toastr';

import { appRoutes } from './app.routes';
import { reducers } from './store';
import { AuthEffects } from './store/auth/auth.effects';
import { PushNotificationsEffects } from './store/push-notifications/push-notifications.effects';
import { authInterceptor } from './interceptors/auth.interceptor';
import { httpErrorInterceptor } from './interceptors/http-error.interceptor';
import { AuthInitializerService } from './services/auth-initializer.service';
import { ChunkErrorHandler } from './services/chunk-error.handler';
import { LucideIconsModule } from './shared/lucide-icons.module';
import { QuillModule } from 'ngx-quill';

export const appConfig: ApplicationConfig = {
  providers: [
    provideAnimations(),
    provideHttpClient(withInterceptors([httpErrorInterceptor, authInterceptor])),
    provideRouter(
      appRoutes,
      // PATH routing, deliberately: a `#fragment` is never sent to the server, so
      // hash routing kept the API Gateway, the Public function and link-preview
      // crawlers blind to the actual route. Deep-link entry needs a server-side
      // SPA fallback, which `client/public/404.html` + `src/main.ts` provide.
      withInMemoryScrolling({ scrollPositionRestoration: 'top', anchorScrolling: 'enabled' })
    ),

    // NgRx
    provideStore(reducers),
    provideEffects([AuthEffects, PushNotificationsEffects]),
    provideStoreDevtools({ maxAge: 25, logOnly: false }),

    importProvidersFrom(ToastrModule.forRoot({
      positionClass: 'toast-bottom-right',
      closeButton: true,
      progressBar: true,
      timeOut: 3500,
      tapToDismiss: true
    })),
    importProvidersFrom(LucideIconsModule),
    importProvidersFrom(QuillModule.forRoot()),

        // Auth Initializer
        {
            provide: APP_INITIALIZER,
            useFactory: () => {
                const authInitializer = inject(AuthInitializerService);
                return () => authInitializer.initializeAuth();
            },
            multi: true,
        },
        {
            provide: ErrorHandler,
            useClass: ChunkErrorHandler
        }
    ]
};
